import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Animated, StyleSheet } from "react-native";

import ToastNotification, {
  ToastNotificationProps,
} from "@/components/common/ToastNotification";

export const ToastContext = createContext<{
  clearMessage: () => void;
  addMessage: ({ label, type, icon, action }: ToastNotificationProps) => void;
}>({
  clearMessage: () => {},
  addMessage: () => {},
});
export const useToast = () => useContext(ToastContext);
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<ToastNotificationProps>({
    label: "",
    type: "default",
    active: false,
    delayBeforeHide: 3500,
  });

  const offset = useRef(new Animated.Value(90)).current;

  const addMessage = useCallback(
    (newMessage: ToastNotificationProps) => {
      setMessage({
        ...newMessage,
        active: true,
      });
    },
    [setMessage]
  );

  const clearMessage = useCallback((): void => {
    setMessage((prevMessage) => ({ ...prevMessage, active: false }));
  }, [setMessage]);

  useEffect(() => {
    const showAnimation = (callback = () => {}) => {
      Animated.timing(offset, {
        toValue: 0,
        duration: 275,
        useNativeDriver: false,
      }).start(callback);
    };

    const hideAnimation = (delay: number, callback = () => {}) => {
      Animated.timing(offset, {
        toValue: 90,
        duration: 275,
        useNativeDriver: false,
        delay,
      }).start(callback);
    };

    if (message.action) {
      if (message.active == true) {
        showAnimation();
      } else {
        hideAnimation(0);
      }
    } else if (message.active === true) {
      showAnimation(() => {
        hideAnimation(message?.delayBeforeHide || 3500);
      });
    }
  }, [message]);

  return (
    <ToastContext.Provider value={{ addMessage, clearMessage }}>
      {children}
      <Animated.View
        style={[styles.root, { transform: [{ translateY: offset }] }]}
      >
        <ToastNotification
          label={message?.label || ""}
          type={message?.type}
          icon={message?.icon}
          action={message?.action}
        />
      </Animated.View>
    </ToastContext.Provider>
  );
};

const styles = StyleSheet.create({
  root: {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    paddingHorizontal: 12,
    paddingVertical: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9,
  },
});
